/**
 * GH Pharmacy - POS Proxy Client
 * Utility functions for calling the pos-proxy edge function from the dashboard.
 *
 * READ actions return { data, source: 'live' | 'cache' | 'offline', cached_at? }
 * WRITE actions return { success, error? }
 */

import { supabase } from './supabase'

export interface PosResponse<T> {
  data: T | null
  source: 'live' | 'cache' | 'offline'
  cached_at?: string
  error?: string
}

/**
 * Send a READ action to the POS proxy.
 * Falls back to cached data if the PC is offline.
 */
export async function posQuery<T = unknown>(
  action: string,
  params?: Record<string, unknown>
): Promise<PosResponse<T>> {
  const { data, error } = await supabase.functions.invoke('pos-proxy', {
    body: { action, params },
  })

  if (error) {
    console.error('[posProxy] Error:', error)
    throw new Error(error.message || 'POS proxy error')
  }

  return data as PosResponse<T>
}

/**
 * Send a WRITE action to the POS proxy.
 * Returns immediately on PC failure — writes are never cached.
 */
export async function posWrite(
  action: string,
  params: Record<string, unknown>
): Promise<{ success: boolean; error?: string }> {
  const { data, error } = await supabase.functions.invoke('pos-proxy', {
    body: { action, params },
  })

  if (error) throw new Error(error.message || 'POS write error')
  return data as { success: boolean; error?: string }
}
